// App.tsx — navigation + route/overlay orchestration.
//
// Two routes hang off Home: "Regulate now" (placeholder/integration overlay)
// and "Continue recovery" (the ordered programme: tabs, module detail, and the
// step viewer overlay). Progress is shared via ProgressProvider.
import React from 'react';
import { AppShell } from '@/components/AppShell';
import { type TabId } from '@/components/TabBar';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { HomeScreen, landTodayAtTop } from '@/screens/HomeScreen';
import { RecoveryHome } from '@/screens/RecoveryHome';
import { ModuleScreen } from '@/screens/ModuleScreen';
import { YourStuffScreen } from '@/screens/YourStuffScreen';
import { LibraryScreen } from '@/screens/LibraryScreen';
import { ArticleReader } from '@/flows/library/ArticleReader';
import { YouScreen } from '@/screens/YouScreen';
import { ProgressScreen } from '@/screens/ProgressScreen';
import { ReviewQueue } from '@/flows/review/ReviewQueue';
import { GuideFlowBeat } from '@/flows/guide/GuideFlowBeat';
import { ExerciseViewer } from '@/flows/toolkit/ExerciseViewer';
import { toolById, type Tool, TOOLS, isToolUnlocked } from '@/data/toolkit';
import { ASK_RENURTURE, TRIAL_FULL_LOCK } from '@/config/features';
import { AskReNurture } from '@/new/AskReNurture';
import { openExternal } from '@/services/openExternal';
import { CalmBreathing } from '@/flows/calm/CalmBreathing';
import { CareReflection } from '@/flows/care/CareReflection';
import { CrisisSupport } from '@/flows/care/CrisisSupport';
import { ProgrammeStepScreen } from '@/flows/recovery/ProgrammeStepScreen';
import { ActBoundary } from '@/flows/recovery/ActBoundary';
import { Onboarding } from '@/flows/onboarding/Onboarding';
import { FirstSession } from '@/flows/onboarding/FirstSession';
import { OnboardingV2 } from '@/flows/onboarding/OnboardingV2';
import { takePostOnboardingStart } from '@/flows/onboarding/postOnboardingStart';
import { ConsentGate } from '@/flows/onboarding/ConsentGate';
import { SoftEntry, softEntrySeen } from '@/flows/onboarding/SoftEntry';
import { IntroJourney } from '@/flows/onboarding/IntroJourney';
import { CONSENT_VERSIONS } from '@/config/consent';
import { fetchLatestConsents } from '@/services/consent';
import { FIRST_SESSION_ONBOARDING, FIRST_SESSION_ONBOARDING_V2, LAND_IN_LESSON_ON_ENTRY, ONBOARDING_2026, TODAY_ONE_JOB, TRIAGE_V2 } from '@/config/features';
import { TriageHome, type TriageView } from '@/flows/triage';
import { TrialEndedSheet } from '@/flows/triage/TrialEndedSheet';
import { captureReturn, restoreView, freshTriageNav, type JumpReturn } from '@/flows/triage/jumpReturn';
import { MeditationPlayer } from '@/flows/meditations/MeditationPlayer';
import { ReflectionEditor } from '@/flows/recovery/ReflectionEditor';
import { MEDITATIONS, type Meditation } from '@/data/meditations';
import { ProgressProvider, useProgress } from '@/hooks/useProgress';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { useSync } from '@/hooks/useSync';
import { LessonFeedbackCard } from '@/flows/research/LessonFeedbackCard';
import { ActSurvey } from '@/flows/research/ActSurvey';
import { canSync, queueLessonFeedback, queueSurvey, pushResearchActSurvey } from '@/services/sync';
import { trackEvent, trackScreen, trackAppOpen } from '@/services/metrics';
import { SignUpWall } from '@/components/SignUpWall';
import { CalmTrialLock } from '@/components/CalmTrialLock';
import { FeatureLockWall } from '@/components/FeatureLockWall';
import { GuideMeLockSheet } from '@/flows/triage/GuideMeLockSheet';
import { MembershipPaywall } from '@/components/MembershipPaywall';
import { usePaywallOpen, closePaywall, openPaywall } from '@/services/paywallStore';
import { SHOW_IAP, SHOW_PURCHASE, PURCHASE_URL } from '@/config/supabase';
import { isFreeToolWhenLocked, isSelfCompassionMeditation } from '@/config/featureLock';
import { InspireOverlay } from '@/components/InspireOverlay';
import { LoginWall } from '@/components/LoginWall';
import { WelcomeScreen } from '@/components/WelcomeScreen';
import type { SavedReflection } from '@/services/progress';
import { useProgramme } from '@/hooks/useProgramme';
import { useFogsReturn } from '@/hooks/useFogsReturn';
import { HoldTools } from '@/new/HoldTools';
import { ToolkitPage } from '@/new/ToolkitPage';
import { COMPASSION_PROGRAMME } from '@/data/compassionProgramme';
import { canOpen } from '@/services/compassionDaily';
import { findStep, courseStatus, isBeyondFreeCap, freeCapStepId, FREE_CAP_STEP_ID, isIntroOrSummaryModule, allSteps, isStepOpenable } from '@/services/contentProvider';
import type { Step } from '@/types';

function Shell() {
  const { programme: mainProgramme, loading, error, reload } = useProgramme();
  const progress = useProgress();
  const auth = useAuth();
  const activeProgramme = progress.state.activeProgramme;
  // Background progress sync + research-post queue flush (fail-soft, no-op offline).
  useSync();
  // Web: receive the full FOGS check's return (done=1&flagged=...) before any early return below.
  useFogsReturn();

  const [tab, setTab] = React.useState<TabId>('today');
  // Pilot behaviour telemetry (anonymous, aggregate, never blocks): one app-open
  // per day (retention tenure) on mount, and a screen-reach count per tab change.
  React.useEffect(() => { trackAppOpen(); }, []);
  React.useEffect(() => { trackScreen(tab); }, [tab]);
  const [moduleId, setModuleId] = React.useState<string | null>(null);
  const [openStepId, setOpenStepId] = React.useState<string | null>(null);
  const [calmProtocol, setCalmProtocol] = React.useState<string | null>(null);
  // When set, the breathing overlay skips the time picker and runs straight into a
  // fixed-length guided session (the "Settle me" arrival shortcut → 2-minute panic reset).
  const [calmImmediateMins, setCalmImmediateMins] = React.useState<number | null>(null);
  // Session-scoped: whether the current breathing session came from the emotion-
  // triggered "A breathing exercise" route (the only one that should offer the
  // "Do CARE next" CTA on Complete). Distinct from progress.state.careTriggered,
  // which is a persisted flag that drives the separate Today-page CARE tile and
  // must not leak into unrelated breathing sessions.
  const [calmCareEligible, setCalmCareEligible] = React.useState(false);
  const [careOpen, setCareOpen] = React.useState(false);
  // TRIAGE_V2: which triage screen Today shows. It lives HERE, not in TriageHome, because
  // choosing Recovery programme / Self-compassion changes activeProgramme, which re-keys the
  // AppShell content (screenKey) and remounts TriageHome in that same render, discarding any
  // local view state (the "first tap bounces back" bug). Reset to 'home' on every entry to
  // Today (goTab, the Self-compassion handoff) and whenever the signed-in user changes, so a
  // route never carries over to the next person on a shared device. Unconditional and above
  // every early return (Rules of Hooks); inert when the flag is off.
  const [triageView, setTriageView] = React.useState<TriageView>('home');
  // TRIAGE_V2: where a cross-tab jump from Today (library theme, programme map) came from, so
  // Back returns to that same triage route instead of the arrival question. Cleared by any tab tap.
  const [returnTo, setReturnTo] = React.useState<JumpReturn | null>(null);
  // TRIAGE_V2: the ex route's "Settle first" step. Pending only while the session it started
  // is running; the counter bumps only when THAT session reaches its natural end.
  const settleFirstPending = React.useRef(false);
  // Needs-check "Settle my body first": land in lesson 1 when that settle session exits.
  const settleThenLessonPending = React.useRef(false);
  const [settleFirstCompletions, setSettleFirstCompletions] = React.useState(0);
  const triageUserId = auth.session?.user.userId ?? null;
  const [triageViewOwner, setTriageViewOwner] = React.useState<string | null>(triageUserId);
  if (triageViewOwner !== triageUserId) {
    // Adjust-state-during-render (not an effect), so the next user never sees one frame
    // of the previous user's route.
    setTriageViewOwner(triageUserId);
    setTriageView(freshTriageNav().view);
    setReturnTo(freshTriageNav().returnTo); // a recorded jump belongs to the previous person
    settleFirstPending.current = false;
    settleThenLessonPending.current = false;
  }
  const [crisisOpen, setCrisisOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  // When settings is opened from the reminder-nudge tile, jump straight to the
  // Reminders section instead of the top of the screen.
  const [settingsFocus, setSettingsFocus] = React.useState<'reminders' | null>(null);
  // Header / side-rail overlays are mutually exclusive and close on navigation, so one
  // can never sit stuck on top of the content. (On the desktop side-rail the rail stays
  // clickable beside the overlay, so without this an overlay looked like a frozen screen.)
  const closeOverlays = () => {
    setSettingsOpen(false); setSettingsFocus(null);
    setCrisisOpen(false); setProgressOpen(false); setInspireOpen(false);
    setReviewQueueOpen(false); setCareOpen(false); setHoldOpen(false); setToolkitOpen(false);
    setGuideMeLockOpen(false);
  };
  const openSettings = (focus: 'reminders' | null = null) => { closeOverlays(); setSettingsFocus(focus); setSettingsOpen(true); };
  const openCrisis = () => { closeOverlays(); setCrisisOpen(true); };
  const openProgress = () => { closeOverlays(); setProgressOpen(true); };
  const openInspire = () => { closeOverlays(); setInspireOpen(true); };
  const [reviewQueueOpen, setReviewQueueOpen] = React.useState(false);
  const [progressOpen, setProgressOpen] = React.useState(false);
  const [inspireOpen, setInspireOpen] = React.useState(false);
  const [openTool, setOpenTool] = React.useState<Tool | null>(null);
  const [editingReflection, setEditingReflection] = React.useState<SavedReflection | null>(null);
  const [openArticleId, setOpenArticleId] = React.useState<string | null>(null);
  const [openMeditation, setOpenMeditation] = React.useState<Meditation | null>(null);
  // Preselected Library theme (set when opening a reading theme from the Difficulty-
  // with-ex screen); cleared when the user leaves the Library tab so a manual visit
  // lands on the library grid.
  const [libraryTheme, setLibraryTheme] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (tab !== 'library' && libraryTheme) setLibraryTheme(null);
  }, [tab, libraryTheme]);
  // Bumped when a chapter completes, so the home feed remounts at the top on return.
  const [completionNonce, setCompletionNonce] = React.useState(0);
  // Bumped when the Today tab is tapped, so its scroll resets to the top even when Today
  // is already the active tab (part of scrollResetKey below).
  const [todayTopNonce, setTodayTopNonce] = React.useState(0);
  // An act-boundary processing screen to show (e.g. 'act1'), or null.
  const [actBoundary, setActBoundary] = React.useState<string | null>(null);
  // The trial Sign-Up content wall (act1-c04-l06 cap), shown when a free user
  // tries to go past the free portion. Hidden entirely for purchased users.
  const [signUpWall, setSignUpWall] = React.useState(false);
  const paywallOpen = usePaywallOpen();
  // The Calm Your Body unlock wall, shown when a trial user opens Calm after the
  // 10-day window has closed. Dismissable — the rest of the app stays open.
  const [calmLockOpen, setCalmLockOpen] = React.useState(false);
  const [featureLockOpen, setFeatureLockOpen] = React.useState(false);
  const [guideMeLockOpen, setGuideMeLockOpen] = React.useState(false);
  // The "Hold" page (boundary & urge tools) and the full Toolkit page, opened from
  // the new Today header — both with a "‹ Today" back button for consistent nav.
  const [holdOpen, setHoldOpen] = React.useState(false);
  const [toolkitOpen, setToolkitOpen] = React.useState(false);
  // Ask ReNurture (dev-mode-gated). State is lifted here (not inside the overlay) so
  // opening a piece of content and returning keeps the question + results live.
  const [askOpen, setAskOpen] = React.useState(false);
  const [askQuery, setAskQuery] = React.useState('');
  const [askResult, setAskResult] = React.useState<any>(null);
  // One-shot request to open the front-page Calm chooser (from Ask's "Calm" button).
  const [openCalmOnToday, setOpenCalmOnToday] = React.useState(false);
  // One-shot: land the user straight in their first lesson (Video 1 for a new user)
  // the moment onboarding finishes — detected as onboardingDone flipping false→true
  // within this mount (completeOnboarding updates state in place, no reload).
  const [openProgrammeOnToday, setOpenProgrammeOnToday] = React.useState(false);
  // "Settle my body first" from the onboarding reveal: when that 2-minute session finishes
  // or is closed, land in lesson 1.
  const prevOnboardingDone = React.useRef(progress.state.onboardingDone);
  React.useEffect(() => {
    // Land in the first lesson only on a GENUINE onboarding completion this session.
    // onboardingJustCompleted (set solely by completeOnboarding) distinguishes that
    // from a returning account's sync/hydration flipping onboardingDone false→true —
    // which would otherwise force existing users into a video on entry.
    if (
      LAND_IN_LESSON_ON_ENTRY &&
      !prevOnboardingDone.current &&
      progress.state.onboardingDone &&
      progress.onboardingJustCompleted.current
    ) {
      // The needs-check reveal hands over the chosen door (null = the entry screen: lesson 1).
      const start = takePostOnboardingStart();
      if (start === 'settle') {
        // Same free 2-minute find_the_ground session as startSettleNow, inlined because that
        // const is declared after App's early returns (calling it here could hit its TDZ).
        settleThenLessonPending.current = true;
        progress.markCalmUsed();
        trackEvent('use', 'calm');
        setCalmImmediateMins(2);
        setCalmProtocol('find_the_ground');
        setCalmCareEligible(false);
      }
      else if (start !== 'explore') setOpenProgrammeOnToday(true);
      progress.onboardingJustCompleted.current = false;
    }
    prevOnboardingDone.current = progress.state.onboardingDone;
  }, [progress.state.onboardingDone]);
  // Research data collection (consent-gated, signed-in users only):
  //   • pendingChapterFeedback — a QUEUE of finished chapters awaiting the
  //     "how was this chapter?" card (a queue, not one value, so finishing several
  //     chapters before a clear screen never drops any — each is asked in turn).
  //   • actSurvey — an act boundary just cleared; offer the 6-dim wellbeing survey.
  const [pendingChapterFeedback, setPendingChapterFeedback] = React.useState<string[]>([]);
  const [actSurvey, setActSurvey] = React.useState<string | null>(null);
  // TODAY_ONE_JOB: post-lesson, one beat may ask — the survey and feedback beats are
  // moved to the next arrival as "When you're ready" cards on Today. The card for
  // chapter feedback opens the existing overlay only when tapped (this latch).
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  // Chapters already offered feedback THIS session. A synchronous guard (refs don't
  // lag like state): chapterFeedbackGiven is the durable record, but it commits a
  // tick later, so a second onChapterComplete for the same chapter — e.g. a double
  // tap on Complete, or the handler re-running before that commit — would re-raise
  // the card "as the next module loads". This makes the prompt strictly once-per-chapter.
  const promptedChaptersRef = React.useRef<Set<string>>(new Set());
  // Research collection requires EXPLICIT consent (granted at the consent gate). The
  // gate guarantees consent is no longer 'unset' by the time we reach here, so this is
  // simply: signed in AND opted in.
  const researchOk = canSync(auth.session) && progress.state.researchConsent === 'granted';

  // Switching track clears any pushed module/step view from the other programme.
  React.useEffect(() => {
    setModuleId(null);
    setOpenStepId(null);
  }, [activeProgramme]);

  // Surface an ACT boundary screen once, at the crossing: an ACT just fully done
  // with the next ACT now current (or it was the final ACT). Covers act1/act2/act3.
  // Only on the main track; shown once each via actBoundariesSeen.
  const completedModulesKey = progress.state.completedModules.join(',');
  React.useEffect(() => {
    if (activeProgramme !== 'main' || !mainProgramme) return;
    const ACTS = ['act1', 'act2', 'act3'];
    for (let i = 0; i < ACTS.length; i++) {
      const id = ACTS[i];
      if (progress.state.actBoundariesSeen.includes(id)) continue;
      const act = mainProgramme.courses?.find((c) => c.id === id);
      if (!act || courseStatus(mainProgramme, act, progress.state) !== 'done') continue;
      const next = mainProgramme.courses?.find((c) => c.id === ACTS[i + 1]);
      if (!next || courseStatus(mainProgramme, next, progress.state) === 'current') {
        setActBoundary(id);
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProgramme, mainProgramme, completedModulesKey, progress.state.actBoundariesSeen]);

  // Hydrate the locked user_type from the profile so onboarding asks ONCE, ever. An
  // existing/already-tagged user (or one signing in on a new device) gets their type
  // from the server, so the onboarding step is skipped and check-in gating works.
  const profileUserType = auth.session?.user?.userType ?? null;
  React.useEffect(() => {
    if (profileUserType && progress.state.userType == null) progress.setUserType(profileUserType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileUserType, progress.state.userType]);

  // Restore the consent decisions this user already made, for the same reason as the
  // user_type hydration above: a reinstall or a new device is not a new person.
  //
  // Terms acceptance and the research decision were only ever kept in device storage,
  // while the demonstrable record has always been in Supabase `consents` (append-only,
  // versioned, RLS-pinned to auth.uid(), with a select-own policy). Nothing read it back,
  // so someone who consented months ago and reinstalled was walked through the whole gate
  // again with their answer sitting in the database the entire time.
  //
  // 'pending' holds the gate shut while we look, so the consent screen never flashes up
  // and then vanishes (the same class of race as the welcome-flash bug). Any failure
  // resolves to 'done' and the user is simply asked, which is the safe direction.
  // Has the soft-entry intro been seen on this device? Read once so the sequence cannot
  // reappear mid-session when the flag is written.
  //
  // ONBOARDING_2026 deliberately does NOT remember it. Someone who walked the intro
  // and then did not create an account gets it again next launch: they left before
  // the app had anything of theirs, so on the next open they are a first-time visitor
  // again and the case for signing up is the thing worth making. Skip sits top-right
  // on every screen (the splash included), so passing it costs one tap. Once there is
  // a session the gate below never fires, and the repeat stops on its own.
  const [softEntryDone, setSoftEntryDone] = React.useState<boolean>(
    () => (ONBOARDING_2026 ? false : softEntrySeen()),
  );

  const signedIn = !!auth.session;
  const localConsentMissing =
    progress.state.researchConsent === 'unset' || progress.state.termsAcceptedVersion == null;
  // Restore what this user already agreed to, so a reinstall or a new device is not
  // mistaken for a new person. Runs ONCE, in the BACKGROUND, and blocks nothing: if it
  // lands the consent gate simply stops applying; if it never lands the user is asked,
  // which is the old behaviour and perfectly survivable. Nothing about entering the app
  // waits on it.
  const restoreRan = React.useRef(false);
  React.useEffect(() => {
    if (!signedIn || !localConsentMissing || restoreRan.current) return;
    restoreRan.current = true;
    fetchLatestConsents()
      .then((rows) => {
        if (!rows) return;
        // Their terms acceptance only counts for the version they actually saw. An older
        // version legitimately re-prompts; that is the point of recording doc_version.
        if (rows.terms?.action === 'accepted' && rows.terms.doc_version === CONSENT_VERSIONS.terms) {
          progress.acceptTerms(rows.terms.doc_version);
        }
        // The log is append-only and a withdrawal is a NEW row, so the newest row IS the
        // current position. 'withdrawn' means not consenting, which locally is 'declined'.
        if (rows.research) {
          const a = rows.research.action;
          if (a === 'granted' || a === 'declined' || a === 'withdrawn') {
            progress.setResearchConsent(a === 'granted' ? 'granted' : 'declined', rows.research.doc_version);
          }
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, localConsentMissing]);

  // A purchase/restore (or any refresh) that makes the user a member closes every unlock wall,
  // so the paywall's Continue lands on the unlocked content. MUST stay above the early
  // returns below: a hook after them crashes the app (React #310) when onboarding ends.
  React.useEffect(() => {
    if (!auth.isMember) return;
    setSignUpWall(false);
    setCalmLockOpen(false);
    setFeatureLockOpen(false);
  }, [auth.isMember]);

  // TRIAL_FULL_LOCK: if the trial lapses while a locked tab/page is open, fall back to Today.
  // (Journey stays reachable on the Self-compassion track: that is its course map.)
  React.useEffect(() => {
    if (!TRIAL_FULL_LOCK || !auth.featuresLocked) return;
    if (tab !== 'today' && !(tab === 'journey' && activeProgramme === 'compassion')) setTab('today');
    setToolkitOpen(false);
    setHoldOpen(false);
  }, [auth.featuresLocked, tab, activeProgramme]);

  if (loading) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100dvh', maxWidth: 520, margin: '0 auto' }}>
        <LoadingState />
      </div>
    );
  }
  if (error || !mainProgramme) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100dvh', maxWidth: 520, margin: '0 auto' }}>
        <ErrorState
          title="We couldn’t open your programme"
          message="Nothing you did is wrong. Please try again in a moment."
          onRetry={reload}
        />
      </div>
    );
  }

  // SOFT ENTRY — the first thing anyone sees, and therefore FIRST in this chain.
  //
  // It must sit ahead of the onboarding gate as well as the sign-in wall. A brand-new
  // user has onboardingDone === false, so putting it after that gate meant OnboardingV2
  // rendered first with its own "Welcome to ReNurture", and these four screens never
  // appeared at all. The whole point is that the app says what it is before it asks
  // anything of you, so nothing may come before it.
  //
  // Once per device, never to someone already signed in, skippable on every screen.
  //
  // ONBOARDING_2026 (on for iOS, Android, AND web) swaps these four screens for the
  // rebuilt six: a splash, then the five value screens from the onboarding deck.
  // Both share the same seen-key, so nobody gets shown two intros. SoftEntry is
  // fully retired in favour of IntroJourney everywhere.
  if (auth.enabled && auth.ready && !auth.session && !softEntryDone) {
    return ONBOARDING_2026
      ? <IntroJourney onDone={() => setSoftEntryDone(true)} />
      : <SoftEntry onDone={() => setSoftEntryDone(true)} />;
  }

  // NOTE: there is deliberately NO loading gate here any more.
  //
  // The restore used to hold the whole app behind a "Welcome back" screen while it ran.
  // That put a network read on the critical path to entering the app, and when it failed
  // to release the app was simply unusable — which happened, twice. A blocking gate in
  // front of everything is the wrong shape for a nicety: the worst case of NOT blocking is
  // that a returning user briefly sees a consent screen that then clears itself, which is
  // a flicker; the worst case of blocking is a door that never opens.
  //
  // The restore now runs in the background. When it lands it updates progress state, this
  // component re-renders, and the consent gate below passes on its own.

  // Onboarding gates the app until worked through. The 1B first-session flow is
  // used by default; the old onboarding is retained behind the flag (reversible).
  if (!progress.state.onboardingDone) {
    // v2 re-sequence (flagged): one controller that also collects consent + the
    // user-type fork up front, so it clears the ConsentGate below on completion.
    if (FIRST_SESSION_ONBOARDING_V2) return <OnboardingV2 />;
    return FIRST_SESSION_ONBOARDING ? <FirstSession /> : <Onboarding />;
  }

  // First entitlement check in flight: show a calm wheel so a user on a poor
  // connection sees why there's a wait instead of a flash of the wrong (signed-
  // out / free) state. Bounded by an 8s read timeout in auth.ts, then it proceeds
  // with the last-known entitlement. (Foreground re-checks are non-blocking.)
  if (auth.enabled && !auth.ready) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100dvh', maxWidth: 520, margin: '0 auto' }}>
        <LoadingState label="Checking your membership…" />
      </div>
    );
  }

  // Hard sign-in gate: with activation on and no session, the app is NOT
  // enterable — the user must sign in or create a free account. Catches anyone
  // who finished onboarding before login was required. LoginWall uses useAuth.
  if (auth.enabled && auth.ready && !auth.session) {
    return <LoginWall />;
  }

  // Consent gate: a signed-in user must accept Terms & Privacy (and is asked once
  // about research) before the app opens. Shown until the CURRENT Terms version is
  // accepted AND the research question has been answered (no longer 'unset'). Bumping
  // CONSENT_VERSIONS.terms re-shows it for everyone, capturing fresh consent.
  //
  // A RETURNING user normally passes straight through: the effect above restores both
  // decisions from the server first, so only a genuine change in what they agreed to
  // stops them here.
  if (
    auth.enabled &&
    auth.session &&
    (progress.state.termsAcceptedVersion !== CONSENT_VERSIONS.terms ||
      (progress.state.userType ?? profileUserType) == null ||
      progress.state.researchConsent === 'unset')
  ) {
    return <ConsentGate />;
  }

  // The active track: the main recovery programme, or the self-compassion soft
  // landing. Selectors are programme-agnostic, so the whole journey section just
  // follows whichever is passed. Progress is shared (ids never collide).
  const isMember = auth.isMember;
  // TRIAL_FULL_LOCK: a lapsed non-member. Every opener below funnels through showLock (the one
  // locked action = FeatureLockWall, which routes to the IAP paywall on iOS) via lockedGuard or
  // an explicit fullLock check. Crisis support, settings and the Self-compassion track never do.
  const fullLock = TRIAL_FULL_LOCK && auth.featuresLocked;
  const showLock = () => setFeatureLockOpen(true);
  const lockedGuard = <A extends unknown[]>(fn: (...a: A) => void) => (...a: A) => {
    if (fullLock) { showLock(); return; }
    fn(...a);
  };
  const tabBlocked = (id: TabId) => fullLock && id !== 'today' && !(id === 'journey' && activeProgramme === 'compassion');
  const calmLockAction = () => (fullLock ? showLock() : setCalmLockOpen(true));
  const baseProgramme = activeProgramme === 'compassion' ? COMPASSION_PROGRAMME : mainProgramme;
  // Programme CONTENT is capped for every non-member: during the 10-day trial at the
  // Sign-Up step (act1-c04-l06 = up to & including chapter 4); after the trial at the
  // end of 1.1 (act1-c03-l17, chapter 3). Members never see the cap step (it IS the
  // wall, meaningless once entitled).
  const withoutCapStep = (p: typeof baseProgramme) => isMember
    ? {
        ...p,
        modules: p.modules.map((m) => ({
          ...m,
          steps: m.steps.filter((s) => s.id !== FREE_CAP_STEP_ID),
        })),
      }
    : p;
  const programme = withoutCapStep(baseProgramme);

  const navTo = (id: TabId, keepTriage: boolean) => {
    // Navigating closes anything open on top of the content (overlays + content viewers),
    // so a tab switch is never hidden behind a stuck overlay (esp. the desktop side-rail).
    closeOverlays();
    setModuleId(null);
    setOpenStepId(null); setOpenArticleId(null); setOpenTool(null); setOpenMeditation(null); setEditingReflection(null);
    setPendingChapterFeedback([]); // navigating away drops any pending feedback — it belongs to the module just finished, not the next screen
    if (id === 'today') {
      // Tapping Today lands at the TOP (arrival header), not the revealed feed — bump a
      // nonce so the scroll resets even when already on Today, and suppress HomeScreen's
      // feed auto-scroll for this mount.
      landTodayAtTop();
      setTodayTopNonce((n) => n + 1);
      if (!keepTriage) setTriageView('home'); // TRIAGE_V2: back to the arrival question (inert when off)
    }
    setTab(id);
  };
  // Every tab tap / "go to X" exit: forgets any recorded jump (a tab tap resets to the question).
  const goTab = (id: TabId) => {
    if (tabBlocked(id)) { showLock(); return; }
    setReturnTo(null);
    navTo(id, false);
  };
  // TRIAGE_V2: a jump out of Today's triage stack. Flag off => identical to goTab.
  const jumpTab = (id: TabId) => {
    if (tabBlocked(id)) { showLock(); return; }
    const ret = captureReturn(TRIAGE_V2, tab, triageView);
    if (!ret) { goTab(id); return; }
    setReturnTo(ret);
    navTo(id, true);
  };
  // Back from a jump: Today again, showing the route the user left (no reset to 'home').
  const goBackFromJump = () => {
    const v = restoreView(returnTo);
    setReturnTo(null);
    if (v === null) { goTab('today'); return; }
    setTriageView(v);
    navTo('today', true);
  };

  // Access model:
  //   • member            → all content open: no Sign-Up cap.
  //   • non-member        → main programme content capped at the Sign-Up step
  //     (trial OR lapsed)    (trial: up to & incl. chapter 4; after the trial: up to & incl. 1.1, chapter 3). Become a
  //                          member to read on. App features + self-compassion stay
  //                          open; the 10-day trial governs feature access and the
  //                          countdown, NOT this content cap.
  //   • ungated           → when activation isn't configured (dev/web), nothing is gated.
  // Cap step: in trial = end of ch4 (Sign-Up step); after the trial = end of 1.1 (ch3); null = none.
  const capId = freeCapStepId(auth);
  // Calm Your Body model (2026-07-14): the STRUCTURED breathing (choose-a-protocol,
  // relaxation, the emotions check-in) becomes a membership feature once the 10-day
  // trial lapses — the breathing entry points gate on auth.featuresLocked (startCalm
  // below) and send lapsed users to the CalmTrialLock unlock wall. The arrival panel
  // degrades gracefully via its `featureLock` prop (a panic-only view), so we do NOT
  // fully hide it: `locked` stays false. ALWAYS free for safety, regardless of
  // membership: the 2-minute panic reset (startSettleNow), the in-panel grounding
  // quick-resets, and crisis support.
  const calmLocked = false;

  // Ask ReNurture: the REAL unlocked set from progress — the app is the source of
  // truth for what she can open now. A PLAIN const (not a hook): it sits below the
  // component's early returns, so a useMemo here would change the hook count between
  // renders. Gated on dev mode, so for a normal user it is just `[]` at zero cost.
  const askUnlockedIds: string[] = ASK_RENURTURE
    ? (() => {
        const ids: string[] = [];
        for (const s of allSteps(programme)) {
          if (isStepOpenable(programme, s, progress.state) && !isBeyondFreeCap(programme, s.id, capId)) ids.push(s.id);
        }
        const completed = new Set(progress.state.completedSteps);
        for (const t of TOOLS) if (isToolUnlocked(t, completed)) ids.push(t.id);
        return ids;
      })()
    : [];

  // Start a Calm Your Body / breathing session, unless the user isn't a member —
  // then show the Calm unlock wall instead (the rest of the app stays open; only
  // this feature is member-gated).
  const startCalm = (protocolId: string, careEligible: boolean = false) => {
    if (fullLock) { showLock(); return; }
    // Structured Calm Your Body (choose-a-protocol / relaxation breathing) is a
    // membership feature once the 10-day trial lapses. A lapsed non-member is sent
    // to the unlock wall; the free panic reset + grounding stay open (startSettleNow).
    if (auth.featuresLocked) {
      setCalmLockOpen(true);
      return;
    }
    progress.markCalmUsed();
    trackEvent('use', 'calm');
    setCalmImmediateMins(null);
    setCalmProtocol(protocolId);
    setCalmCareEligible(careEligible);
  };

  // "Settle me" arrival shortcut: jump straight into a 2-minute panic-reset breathing
  // session (no state pick, no time picker). Exiting returns to wherever we were —
  // for the arrival screen that's the "how are you arriving?" question, unchanged.
  const startSettleNow = () => {
    if (fullLock) { showLock(); return; }
    // The arrival "Settle me" shortcut: a 2-minute OVERWHELM reset (find_the_ground),
    // not the panic protocol — the panic reset stays reachable from Calm Your Body.
    // ALWAYS stays free — a survivor in distress must be able to settle regardless of
    // membership. Never gate this on the trial lock.
    progress.markCalmUsed();
    trackEvent('use', 'calm');
    setCalmImmediateMins(2);
    setCalmProtocol('find_the_ground');
    setCalmCareEligible(false);
  };

  // TRIAGE_V2 only: the triggered route's "Breathe with me" — the same free 2-minute
  // find_the_ground session as startSettleNow, but eligible for the "Do CARE next?" offer
  // on the Complete screen (mirrors the emotions flow: triggerCare + careEligible).
  const startSettleNowWithCare = () => {
    if (fullLock) { showLock(); return; }
    progress.markCalmUsed();
    progress.triggerCare();
    trackEvent('use', 'calm');
    setCalmImmediateMins(2);
    setCalmProtocol('find_the_ground');
    setCalmCareEligible(true);
  };

  // Premium features lock once the trial lapses (auth.featuresLocked): opening a
  // locked guided meditation or toolkit exercise shows the unlock wall instead.
  // Grounding/safety tools and roll-the-dice stay free (allowlist) and open as
  // normal; members and trial users are never locked.
  const openMeditationGated = (m: Meditation) => {
    if (fullLock ? !isSelfCompassionMeditation(m.title) : auth.featuresLocked) { showLock(); return; }
    trackEvent('open', 'meditation', m.id);
    setOpenMeditation(m);
  };
  const openToolGated = (t: Tool) => {
    if (fullLock || (auth.featuresLocked && !isFreeToolWhenLocked(t.id))) { showLock(); return; }
    progress.markToolUsed();
    trackEvent('open', 'tool', t.id);
    if (t.linkUrl) openExternal(t.linkUrl);
    else setOpenTool(t);
  };

  const openStep = (step: Step, bypassDayGate = false) => {
    // Self-compassion day gate: one exercise per calendar day, whichever door is used.
    // Review mode (clinician tool) passes bypassDayGate so it can open anything.
    if (!bypassDayGate && step.id.startsWith('sc-') && !canOpen(COMPASSION_PROGRAMME, step, progress.state, !!progress.state.devMode)) return;
    // TRIAL_FULL_LOCK: only Self-compassion lessons open; every main-programme lesson is locked.
    if (fullLock && !findStep(COMPASSION_PROGRAMME, step.id)) { showLock(); return; }
    // Check the main programme too: a main lesson can be opened while the self-compassion
    // track is active (Ask citations, Your Stuff), and that track has no cap step of its own.
    if (isBeyondFreeCap(programme, step.id, capId) || isBeyondFreeCap(mainProgramme, step.id, capId)) {
      setSignUpWall(true);
      return;
    }
    progress.setLastRoute('recovery');
    trackEvent('open', 'lesson', step.id);
    // Opening the next lesson dismisses any pending chapter feedback: it is shown at the
    // END of the module it belongs to (on the clear screen right after), and never carried
    // into the module you're now opening.
    setPendingChapterFeedback([]);
    setOpenStepId(step.id);
  };

  const openCare = lockedGuard(() => setCareOpen(true));
  const openAsk = lockedGuard(() => { closeOverlays(); setAskOpen(true); });
  const openHold = lockedGuard(() => { closeOverlays(); setHoldOpen(true); });
  const openToolkitTab = lockedGuard(() => { closeOverlays(); if (TRIAGE_V2) goTab('toolkit'); else setToolkitOpen(true); });
  const openArticle = lockedGuard((id: string) => { trackEvent('open', 'article', id); setOpenArticleId(id); });

  // Resolve the open step against the active track, falling back to the main
  // programme — so a main-programme reflection opened from Your Stuff works even
  // while the self-compassion track is active.
  const stepProgramme =
    openStepId && !findStep(programme, openStepId) && findStep(mainProgramme, openStepId)
      ? mainProgramme
      : programme;
  const openStepObj = openStepId ? findStep(stepProgramme, openStepId) : undefined;

  // active screen (module detail replaces the tab screen, like a pushed view)
  let screen: React.ReactNode;
  if (toolkitOpen) {
    // Toolkit is a pushed view INSIDE the app shell (header + bottom nav stay put,
    // like Your stuff) rather than a full-screen overlay — with a back arrow, not "Today".
    screen = (
      <ToolkitPage
        onOpenTool={(toolId) => { const t = toolById(toolId); if (t) openToolGated(t); }}
        onClose={() => { landTodayAtTop(); setToolkitOpen(false); }}
      />
    );
  } else if (moduleId) {
    screen = (
      <ModuleScreen
        programme={programme}
        moduleId={moduleId}
        onBack={() => setModuleId(null)}
        onOpenStep={openStep}
      />
    );
  } else if (tab === 'today') {
    // The new arrival-first header (behind NEW_UI) renders INSIDE HomeScreen, above
    // the real course feed — it's a top-half swap, not a screen replacement. These
    // extra callbacks let that header reach the toolkit tab + the Hold tools page.
    const renderToday = (triage?: React.ComponentProps<typeof HomeScreen>['triage']) => (
      <HomeScreen
        triage={triage}
        onOpenToolkit={openToolkitTab}
        showAsk={ASK_RENURTURE}
        onOpenAsk={openAsk}
        onOpenHold={openHold}
        // TRIAGE_V2: TriageHome is the single opener for both (it consumes these on mount);
        // HomeScreen must not open them a second time. Off => unchanged.
        openCalmOnMount={TRIAGE_V2 ? false : openCalmOnToday}
        onCalmOpened={() => setOpenCalmOnToday(false)}
        openProgrammeOnMount={TRIAGE_V2 ? false : openProgrammeOnToday}
        onProgrammeOpened={() => setOpenProgrammeOnToday(false)}
        programme={programme}
        onStartBreathing={startCalm}
        onSettleNow={startSettleNow}
        onOpenCare={openCare}
        onOpenCrisis={openCrisis}
        onOpenStep={openStep}
        calmLocked={calmLocked}
        featureLock={auth.featuresLocked}
        onCalmLocked={calmLockAction}
        onUnlock={showLock}
        onOpenJourney={() => jumpTab('journey')}
        onRollDice={() => (fullLock ? showLock() : openExternal('https://www.renurture.org/compassion-dice'))}
        onOpenSettings={() => openSettings('reminders')}
        onInspire={openInspire}
        onOpenTool={(toolId) => {
          const t = toolById(toolId);
          if (t) openToolGated(t);
        }}
        onOpenLibraryTheme={(theme) => { setLibraryTheme(theme); jumpTab('library'); }}
        onOpenProgress={openProgress}
        // TODAY_ONE_JOB: the post-lesson asks, deferred to the next arrival. The act
        // survey is derived from durable state (boundary seen, survey not yet given)
        // so it survives restarts; chapter feedback rides the session queue.
        deferredActSurvey={
          TODAY_ONE_JOB && researchOk
            ? (progress.state.actBoundariesSeen ?? []).find((id) => !progress.state.actSurveysGiven.includes(id)) ?? null
            : null
        }
        onOpenActSurvey={(id) => setActSurvey(id)}
        pendingChapterFeedback={TODAY_ONE_JOB ? pendingChapterFeedback : []}
        onOpenChapterFeedback={() => setFeedbackOpen(true)}
      />
    );
    // TRIAGE_V2 switch point. A build-time constant, and TriageHome owns its own hooks, so
    // this can never change App's hook order. Off => exactly the original HomeScreen.
    screen = TRIAGE_V2 ? (
      <TriageHome
        programme={programme}
        mainProgramme={activeProgramme === 'compassion' ? withoutCapStep(mainProgramme) : programme}
        renderToday={renderToday}
        view={triageView}
        setView={setTriageView}
        resetNonce={todayTopNonce}
        settleFirstCompletions={settleFirstCompletions}
        onSettleFirst={() => { if (fullLock) { showLock(); return; } settleFirstPending.current = true; startSettleNow(); }}
        onOpenStep={openStep}
        onProgrammeOpened={() => setOpenProgrammeOnToday(false)}
        deferredActSurvey={
          TODAY_ONE_JOB && researchOk
            ? (progress.state.actBoundariesSeen ?? []).find((id) => !progress.state.actSurveysGiven.includes(id)) ?? null
            : null
        }
        onOpenActSurvey={(id) => setActSurvey(id)}
        pendingChapterFeedback={TODAY_ONE_JOB ? pendingChapterFeedback : []}
        onOpenChapterFeedback={() => setFeedbackOpen(true)}
        onOpenCrisis={openCrisis}
        onOpenAsk={openAsk}
        onUnlock={() => setGuideMeLockOpen(true)}
        onLocked={showLock}
        onTrialCountdown={SHOW_IAP ? () => openPaywall() : SHOW_PURCHASE && PURCHASE_URL ? () => { window.open(PURCHASE_URL, '_blank', 'noopener'); } : undefined}
        onOpenToolkit={openToolkitTab}
        onOpenCare={openCare}
        onOpenJourney={() => jumpTab('journey')}
        onOpenTool={(toolId) => { const t = toolById(toolId); if (t) openToolGated(t); }}
        onOpenMeditation={openMeditationGated}
        onOpenLibraryTheme={(theme) => { setLibraryTheme(theme); jumpTab('library'); }}
        onStartBreathing={startCalm}
        onSettleNowWithCare={startSettleNowWithCare}
        calmLocked={calmLocked}
        featureLock={auth.featuresLocked}
        onCalmLocked={calmLockAction}
        openCalmOnMount={openCalmOnToday}
        onCalmOpened={() => setOpenCalmOnToday(false)}
        openProgrammeOnMount={openProgrammeOnToday}
      />
    ) : renderToday();
  } else if (tab === 'toolkit') {
    screen = <ToolkitPage onOpenTool={(toolId) => { const t = toolById(toolId); if (t) openToolGated(t); }} />;
  } else if (tab === 'journey') {
    screen = (
      <RecoveryHome
        programme={programme}
        onOpenModule={(id) => setModuleId(id)}
        onOpenStep={openStep}
        onBack={TRIAGE_V2 && returnTo ? goBackFromJump : undefined}
      />
    );
  } else if (tab === 'library') {
    screen = <LibraryScreen initialLib={libraryTheme} onExit={() => (returnTo ? goBackFromJump() : goTab('today'))} onOpenArticle={openArticle} />;
  } else {
    screen = (
      <YourStuffScreen
        programme={programme}
        onOpenReflection={(r) => setEditingReflection(r)}
        onOpenStep={openStep}
        onOpenMeditation={openMeditationGated}
        onOpenLibraryArticle={openArticle}
      />
    );
  }

  return (
    <AppShell
      tab={tab}
      onTab={goTab}
      tabsLocked={fullLock}
      onTabLocked={showLock}
      onOpenSettings={() => openSettings()}
      onOpenProgress={openProgress}
      // TODAY_ONE_JOB: the "Your recovery" chart icon opens an empty room on day one,
      // which teaches users not to tap it again. Hide it until there is a story to
      // tell (a lesson, a calm session, a tool use, or a recorded check-in).
      showProgress={
        !TODAY_ONE_JOB ||
        progress.state.completedSteps.length > 0 ||
        (progress.state.calmSessionsCount ?? 0) > 0 ||
        (progress.state.toolUsesCount ?? 0) > 0 ||
        (progress.state.completedCheckinCount ?? 0) > 0
      }
      onInspire={openInspire}
      onOpenHelp={openCrisis}
      screenKey={activeProgramme + tab + (moduleId ?? '') + (toolkitOpen ? 'toolkit' : '') + completionNonce}
      scrollResetKey={tab + (moduleId ?? '') + (toolkitOpen ? 'toolkit' : '') + todayTopNonce}
      overlays={
        <>
          {settingsOpen && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 75,
                background: 'var(--cream)',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
              }}
            >
              <YouScreen
                focus={settingsFocus}
                onClose={() => { setSettingsOpen(false); setSettingsFocus(null); }}
                onOpenReview={() => { setSettingsOpen(false); setReviewQueueOpen(true); }}
              />
            </div>
          )}
          {reviewQueueOpen && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 76,
                background: 'var(--cream)',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
              }}
            >
              <ReviewQueue
                programme={programme}
                onOpenStep={(s) => openStep(s, true)}
                onClose={() => setReviewQueueOpen(false)}
              />
            </div>
          )}
          {progressOpen && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 75,
                background: 'var(--cream)',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
              }}
            >
              <ProgressScreen
                onClose={() => setProgressOpen(false)}
              />
            </div>
          )}
          {calmProtocol && (
            <CalmBreathing
              key={calmProtocol + (calmImmediateMins != null ? `-now${calmImmediateMins}` : '')}
              protocolId={calmProtocol}
              startImmediately={calmImmediateMins != null}
              presetMinutes={calmImmediateMins ?? undefined}
              onExit={() => {
                if (TRIAGE_V2) settleFirstPending.current = false;
                setCalmProtocol(null); setCalmImmediateMins(null); setCalmCareEligible(false);
                if (settleThenLessonPending.current) { settleThenLessonPending.current = false; setOpenProgrammeOnToday(true); }
              }}
              showCareNext={calmCareEligible}
              onSessionCompleted={TRIAGE_V2 ? () => {
                if (!settleFirstPending.current) return;
                settleFirstPending.current = false;
                setSettleFirstCompletions((n) => n + 1);
              } : undefined}
              onOpenCare={() => { if (TRIAGE_V2) settleFirstPending.current = false; setCalmProtocol(null); setCalmImmediateMins(null); setCalmCareEligible(false); openCare(); }}
            />
          )}
          {careOpen && <CareReflection onClose={() => setCareOpen(false)} onOpenCrisis={openCrisis} />}
          {crisisOpen && <CrisisSupport onClose={() => setCrisisOpen(false)} />}
          {holdOpen && (
            <HoldTools
              onOpenTool={(toolId) => { const t = toolById(toolId); if (t) openToolGated(t); }}
              onClose={() => setHoldOpen(false)}
            />
          )}
          {openStepObj && (
            <ProgrammeStepScreen
              key={openStepObj.id}
              programme={stepProgramme}
              step={openStepObj}
              onClose={() => setOpenStepId(null)}
              onStartBreathing={(protocolId) => {
                setOpenStepId(null);
                if (auth.featuresLocked) {
                  calmLockAction();
                  return;
                }
                progress.markCalmUsed();
                setCalmProtocol(protocolId);
                setCalmCareEligible(false);
              }}
              onSelfCompassion={
                activeProgramme === 'main'
                  ? () => {
                      setTab('today');
                      // TRIAGE_V2: back to the arrival question, forgetting any recorded jump (inert when off)
                      setTriageView(freshTriageNav().view);
                      setReturnTo(freshTriageNav().returnTo);
                      progress.setActiveProgramme('compassion');
                    }
                  : undefined
              }
              onOpenMeditations={() => {
                setOpenStepId(null);
                goTab('yourstuff');
              }}
              onOpenStep={openStep}
              onExitToToday={() => {
                setOpenStepId(null);
                if (TRIAGE_V2 && returnTo) { goBackFromJump(); return; }
                // TRIAGE_V2: a lesson opened over a triage route closes back to that route.
                if (TRIAGE_V2 && tab === 'today') return;
                goTab('today');
              }}
              onModuleComplete={() => setCompletionNonce((n) => n + 1)}
              onChapterComplete={(chapterId) => {
                // Content feedback is anonymous, formative data — collected from EVERY
                // signed-in user (not just research-consented), on every content chapter,
                // skipping each act's intro and summary modules.
                // act1-c06 is the 1-minute "Super Fast Guide to Journalling" tip — a
                // quick aside, not a full chapter, so skip the 3-question feedback there.
                if (
                  canSync(auth.session) &&
                  chapterId !== 'act1-c06' &&
                  !promptedChaptersRef.current.has(chapterId) &&
                  !progress.state.chapterFeedbackGiven.includes(chapterId) &&
                  !isIntroOrSummaryModule(programme, chapterId)
                ) {
                  promptedChaptersRef.current.add(chapterId);
                  // Queue it (don't overwrite): finishing several chapters before a
                  // clear screen would otherwise drop all but the last and mark them
                  // "prompted" forever. Each main chapter is asked in turn instead.
                  setPendingChapterFeedback((q) => (q.includes(chapterId) ? q : [...q, chapterId]));
                }
              }}
            />
          )}
          {editingReflection && (
            <ReflectionEditor
              key={editingReflection.stepId}
              reflection={editingReflection}
              onClose={() => setEditingReflection(null)}
            />
          )}
          {openArticleId && (
            <ArticleReader key={openArticleId} id={openArticleId} onClose={() => setOpenArticleId(null)} />
          )}
          {openMeditation && (
            <MeditationPlayer key={openMeditation.id} meditation={openMeditation} onClose={() => setOpenMeditation(null)} />
          )}
          {openTool && (
            <ExerciseViewer key={openTool.id} tool={openTool} onClose={() => setOpenTool(null)} />
          )}
          {actBoundary && !openStepObj && (
            <ActBoundary
              actId={actBoundary as 'act1' | 'act2' | 'act3'}
              onContinue={() => {
                const id = actBoundary;
                progress.markActBoundarySeen(id);
                setActBoundary(null);
                // TODAY_ONE_JOB: don't chain the survey onto the boundary — it waits on
                // Today as a "When you're ready" card (derived from actBoundariesSeen
                // minus actSurveysGiven, so it survives restarts until answered).
                if (!TODAY_ONE_JOB && researchOk && !progress.state.actSurveysGiven.includes(id)) setActSurvey(id);
                // queued chapter feedback is kept; the act survey just renders first
                goTab('today');
              }}
              onTakeTime={() => {
                const id = actBoundary;
                progress.markActBoundarySeen(id);
                setActBoundary(null);
                if (!TODAY_ONE_JOB && researchOk && !progress.state.actSurveysGiven.includes(id)) setActSurvey(id);
                // queued chapter feedback is kept; the act survey just renders first
                goTab('today');
              }}
              onOpenMeditations={() => {
                progress.markActBoundarySeen(actBoundary);
                setActBoundary(null);
                goTab('yourstuff');
              }}
              onOpenCalm={() => {
                progress.markActBoundarySeen(actBoundary);
                setActBoundary(null);
                goTab('today');
              }}
              onOpenMap={() => {
                progress.markActBoundarySeen(actBoundary);
                setActBoundary(null);
                goTab('journey');
              }}
              onOpenTools={() => {
                progress.markActBoundarySeen(actBoundary);
                setActBoundary(null);
                // Toolkit now lives on the front page (ToolkitPage), not the Your Stuff tab.
                if (fullLock) showLock();
                else if (TRIAGE_V2) goTab('toolkit');
                else setToolkitOpen(true);
              }}
            />
          )}
          {signUpWall && <SignUpWall onRefresh={auth.recheck} onClose={() => setSignUpWall(false)} />}
          {calmLockOpen && <CalmTrialLock onRefresh={auth.recheck} onClose={() => setCalmLockOpen(false)} />}
          {guideMeLockOpen && <GuideMeLockSheet onSeeMembership={() => { setGuideMeLockOpen(false); setFeatureLockOpen(true); }} onClose={() => setGuideMeLockOpen(false)} onTry={() => { setGuideMeLockOpen(false); openAsk(); }} userId={auth.session?.user.userId ?? null} />}
          {featureLockOpen && <FeatureLockWall onRefresh={auth.recheck} onClose={() => setFeatureLockOpen(false)} trialActive={auth.trialActive} />}
          <TrialEndedSheet
            blocked={crisisOpen || !!calmProtocol || !!openStepObj || !!actBoundary || signUpWall || calmLockOpen || featureLockOpen || guideMeLockOpen || (SHOW_IAP && paywallOpen)}
            onSeeMembership={showLock}
          />
          {SHOW_IAP && paywallOpen && <MembershipPaywall onClose={closePaywall} />}
          {inspireOpen && <InspireOverlay onClose={() => setInspireOpen(false)} />}
          {/* Guide beats (H5 mastery, H7 over-pacing) appear as a step in the flow. */}
          <GuideFlowBeat />
          {/* Research collection — consent-gated, signed-in users only. An act survey
              outranks a lesson card; both wait until no bigger overlay is showing. */}
          {(() => {
            const token = canSync(auth.session) ? auth.session.accessToken : null;
            if (!token) return null;
            // Chapter feedback waits behind any bigger overlay AND the chapter's own
            // celebration, so the card appears after the "well done" moment, not on it.
            const blocked = openStepObj || actBoundary || signUpWall || calmLockOpen || featureLockOpen || !!progress.state.chapterCelebration;
            const surveyDue = actSurvey && !actBoundary && !openStepObj;
            // TODAY_ONE_JOB: the feedback card never auto-opens after a lesson — it
            // renders only when its "When you're ready" card on Today was tapped.
            const feedbackDue = pendingChapterFeedback.length > 0 && !blocked && !actSurvey && (!TODAY_ONE_JOB || feedbackOpen);
            if (!surveyDue && !feedbackDue) return null;
            // Consent is captured up-front at the consent gate, so there's no lazy
            // ask here any more — researchOk already required 'granted' upstream.

            if (surveyDue) {
              const id = actSurvey as string;
              return (
                <ActSurvey
                  actId={id}
                  onSubmit={(scores) => {
                    void queueSurvey(token, id, scores);
                    // Also mirror into the PID-linked research store (consent is already
                    // required to reach the survey — researchOk gates setActSurvey above).
                    if (researchOk) void pushResearchActSurvey(token, id, scores);
                    progress.markActSurveyGiven(id);
                    setActSurvey(null);
                  }}
                  onSkip={() => {
                    progress.markActSurveyGiven(id);
                    setActSurvey(null);
                  }}
                />
              );
            }
            const cid = pendingChapterFeedback[0] as string;
            return (
              <LessonFeedbackCard
                onSubmit={(sentiment, note) => {
                  void queueLessonFeedback(token, cid, sentiment, note);
                  progress.markChapterFeedbackGiven(cid);
                  setPendingChapterFeedback((q) => q.slice(1));
                  setFeedbackOpen(false);
                }}
                onSkip={() => {
                  progress.markChapterFeedbackGiven(cid);
                  setPendingChapterFeedback((q) => q.slice(1));
                  setFeedbackOpen(false);
                }}
              />
            );
          })()}
          {/* The Ask ReNurture trigger lives as a small button under the Today actions
              (see HomeScreen → ArrivalHeader), not a floating pill. */}
          {ASK_RENURTURE && askOpen && (
            <AskReNurture
              onClose={() => setAskOpen(false)}
              hasChildren={true}
              unlocked={askUnlockedIds}
              authToken={canSync(auth.session) ? (auth.session.accessToken ?? null) : null}
              // Research capture is consent-gated at the source: without this the server
              // writes nothing and the feedback row never appears.
              researchConsented={progress.state.researchConsent === 'granted'}
              query={askQuery}
              result={askResult}
              setQuery={setAskQuery}
              setResult={setAskResult}
              onOpenLesson={(id) => { const s = findStep(programme, id) || findStep(mainProgramme, id); if (s) openStep(s); }}
              onOpenArticle={(id) => { if (fullLock) showLock(); else setOpenArticleId(id); }}
              onOpenTool={(id) => { const t = toolById(id); if (t) openToolGated(t); }}
              onOpenBreath={(id) => startCalm(id)}
              onOpenMeditation={(id) => { const m = MEDITATIONS.find((x) => x.id === id); if (m) openMeditationGated(m); }}
              onOpenCalm={() => { setAskOpen(false); setOpenCalmOnToday(true); goTab('today'); }}
              onUnlock={() => { setAskOpen(false); setFeatureLockOpen(true); }}
              userId={auth.session?.user.userId ?? null}
            />
          )}
        </>
      }
    >
      {screen}
    </AppShell>
  );
}

export default function App() {
  return (
    <ProgressProvider>
      <AuthProvider>
        <Shell />
        <WelcomeScreen />
      </AuthProvider>
    </ProgressProvider>
  );
}
